import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from '@tanstack/react-location';
import { useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';

interface Property {
    id: number;
    name: string;
    minNights: number;
    created: string;
    updated: string;
}

interface PropertySwitcherProps {
    currentPropertyId: number;
}

const PropertySwitcher = ({ currentPropertyId }: PropertySwitcherProps) => {
    const [properties, setProperties] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [mobileMenuAnchor, setMobileMenuAnchor] =
        useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                setLoading(true);
                const response = await apiRawFetch('api/properties');

                if (!response.ok) {
                    throw new Error('Failed to fetch properties');
                }

                const data = await response.json();
                setProperties(data);
            } catch (err) {
                console.error('Failed to load properties:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, []);

    const navigateToProperty = (newPropertyId: number) => {
        // Extract the current path without the property ID
        const currentPath = location.current.pathname;
        // Remove the leading /:propertyId
        const pathWithoutPropertyId = currentPath.replace(/^\/\d+/, '');

        // Navigate to the new property with the same page
        navigate({ to: `/${newPropertyId}${pathWithoutPropertyId}` });
    };

    const handlePropertyChange = (event: SelectChangeEvent<number>) => {
        const newPropertyId = event.target.value as number;
        navigateToProperty(newPropertyId);
    };

    const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleMobilePropertySelect = (propertyId: number) => {
        handleMobileMenuClose();
        navigateToProperty(propertyId);
    };

    if (loading || properties.length <= 1) {
        return null;
    }

    const currentProperty = properties.find(p => p.id === currentPropertyId);

    // Mobile: Icon button with menu
    if (isSmallScreen) {
        return (
            <>
                <IconButton
                    onClick={handleMobileMenuOpen}
                    aria-label={`Switch property (current: ${currentProperty?.name ?? 'Unknown'})`}
                    sx={{
                        border: '2px solid #6366f1',
                        borderRadius: 0,
                        backgroundColor: '#1a1a2e',
                        color: '#6366f1',
                        '&:hover': {
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        },
                    }}
                >
                    <SwapHorizIcon />
                </IconButton>
                <Menu
                    anchorEl={mobileMenuAnchor}
                    open={Boolean(mobileMenuAnchor)}
                    onClose={handleMobileMenuClose}
                    slotProps={{
                        paper: {
                            sx: {
                                borderRadius: 0,
                                border: '2px solid #6366f1',
                                backgroundColor: '#1a1a2e',
                                mt: 0.5,
                            },
                        },
                    }}
                >
                    {properties.map(property => (
                        <MenuItem
                            key={property.id}
                            onClick={() =>
                                handleMobilePropertySelect(property.id)
                            }
                            selected={property.id === currentPropertyId}
                            sx={{
                                borderLeft:
                                    property.id === currentPropertyId
                                        ? '4px solid #f59e0b'
                                        : '4px solid transparent',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.875rem',
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                    borderLeftColor: '#6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    transform: 'translateX(2px)',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(99, 102, 241, 0.1)',
                                    },
                                },
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.875rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {property.name}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            </>
        );
    }

    // Desktop: Full dropdown with property name
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                alignSelf: 'stretch',
                gap: 1.5,
                backgroundColor: '#1a1a2e',
                border: '2px solid #6366f1',
                borderRadius: 0,
                px: 2,
            }}
        >
            <SwapHorizIcon
                sx={{
                    color: '#6366f1',
                    fontSize: '1.25rem',
                }}
            />
            <FormControl
                variant="standard"
                sx={{
                    minWidth: 180,
                    '& .MuiInput-root': {
                        '&:before': {
                            borderBottom: 'none',
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: 'none',
                        },
                        '&:after': {
                            borderBottom: 'none',
                        },
                    },
                }}
            >
                <Select
                    value={currentPropertyId}
                    onChange={handlePropertyChange}
                    disableUnderline
                    sx={{
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.875rem',
                        color: '#fff',
                        '& .MuiSelect-icon': {
                            color: '#6366f1',
                        },
                    }}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                borderRadius: 0,
                                border: '2px solid #6366f1',
                                backgroundColor: '#1a1a2e',
                                mt: 0.5,
                            },
                        },
                    }}
                >
                    {properties.map(property => (
                        <MenuItem
                            key={property.id}
                            value={property.id}
                            sx={{
                                borderLeft:
                                    property.id === currentPropertyId
                                        ? '4px solid #f59e0b'
                                        : '4px solid transparent',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontSize: '0.875rem',
                                py: 1.5,
                                px: 2,
                                '&:hover': {
                                    borderLeftColor: '#6366f1',
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    transform: 'translateX(2px)',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                    '&:hover': {
                                        backgroundColor:
                                            'rgba(99, 102, 241, 0.1)',
                                    },
                                },
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: '0.875rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                    }}
                                >
                                    {property.name}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export default PropertySwitcher;
